import { fg } from '@atlaskit/platform-feature-flags';
import { createProvisionalConversationAgent } from '@atlassian/conversation-assistant-store/controllers/chat/conversation-agent-identity';
import { StoreActionLoggableName } from '@atlassian/conversation-assistant-store/controllers/chat/types';

import { createConversationOptimistically } from './createConversationOptimistically';
import { createSafeAction, type SafeAction } from './createSafeAction';
import { fetchConversation } from './fetchConversation';
import { setConversationData } from './setConversationData';
import { setSelectedConversation } from './setSelectedConversation';
import type { ChatStoreAction } from './types';

type HandleChatOpenPubsubActionArgs = { conversationId: string; agentId?: string };

export const handleChatOpenPubsubAction: SafeAction<[HandleChatOpenPubsubActionArgs]> =
	createSafeAction(
		StoreActionLoggableName.handleChatOpenPubsubAction,
		({ conversationId, agentId }: HandleChatOpenPubsubActionArgs): ChatStoreAction =>
			async ({ getState, dispatch }) => {
				// IF convo id is the current chat
				// refetch the chat, do not create new one
				if (conversationId === getState().currentConversationId) {
					// Keep the agent id from this `chat-new` event. When the new selected-agent
					// identity model is off, preserve the older hydration gate as the kill switch.
					const shouldStoreChatNewAgentId = !!agentId && fg('rovo_chat_agent_selection');
					if (shouldStoreChatNewAgentId) {
						const conversationAgentFromChatNew = fg('rovo_chat_agent_selection')
							? createProvisionalConversationAgent(agentId)
							: undefined;
						const conversationAgentFieldsFromChatNew = conversationAgentFromChatNew
							? { agent: conversationAgentFromChatNew }
							: {};
						dispatch(
							setConversationData({
								conversationId,
								data: {
									sourceAgentId: agentId,
									...conversationAgentFieldsFromChatNew,
								},
							}),
						);
					}
					dispatch(fetchConversation({ conversationId }));
					return;
				}

				// IF chat already exist in conversation
				// switch to that convo -> and it will trigger refetch when ChatHistory is rendered
				if (getState().conversations[conversationId]) {
					// Keep the agent id from this `chat-new` event. When the new selected-agent
					// identity model is off, preserve the older hydration gate as the kill switch.
					const shouldStoreChatNewAgentId = !!agentId && fg('rovo_chat_agent_selection');
					if (shouldStoreChatNewAgentId) {
						const conversationAgentFromChatNew = fg('rovo_chat_agent_selection')
							? createProvisionalConversationAgent(agentId)
							: undefined;
						const conversationAgentFieldsFromChatNew = conversationAgentFromChatNew
							? { agent: conversationAgentFromChatNew }
							: {};
						dispatch(
							setConversationData({
								conversationId,
								data: {
									sourceAgentId: agentId,
									...conversationAgentFieldsFromChatNew,
								},
							}),
						);
					}
					dispatch(
						setSelectedConversation({
							conversationId,
						}),
					);
					return;
				}

				// IF chat does not exist yet
				// Create conversation optimistically, which will switch to that conversation
				// By switching conversation, it will render ChatHistory
				// ChatHistory will auto fetch the chat data
				// if not found during `sendMessage`, it will auto create one and retry when encountered `404`
				// Create conversation optimistically and store sourceAgentId
				const sourceAgentIdFromChatNew =
					agentId && fg('rovo_chat_agent_selection') ? agentId : undefined;
				dispatch(
					createConversationOptimistically({
						id: conversationId,
						sourceAgentId: sourceAgentIdFromChatNew,
					}),
				);
			},
	);
